import { createApi } from "@reduxjs/toolkit/query/react";
import {
  addUserType,
  OrganizationChild,
} from "@src/pages/creation-forms/types&validationForms";
import { Organization } from "@src/pages/OrganizationDetailsList";
import { GetAllOrganizationsResponse } from "@src/pages/super-admin/organization/organization-table-component/types";
import { baseQueryWithReauth } from "@src/utils/baseQuery";

export interface GetOrganizationResponse {
  college: Organization[];
  finishedSchool: Organization[];
  institute: Organization[];
  office: Organization[];
}

export interface ApiResponse {
  statusCode: number;
  httpStatus: string;
  response: GetOrganizationResponse;
}

export interface GetOrganizationForSuperTrainer {
  id: string;
  name: string;
  type: string;
  syllabusId: string;
  orgCategory: string;
}
export interface OrganizationResponseForSuperTrainer {
  statusCode: number;
  httpStatus: string;
  response: {
    content: GetOrganizationForSuperTrainer[];
  };
}

export interface GetQuizGroupConfigurationResponse {
  statusCode: number;
  httpStatus: string;
  response: string[];
}

export const OrganizationApiSlice = createApi({
  reducerPath: "organizationApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["getAllOrganizations"],
  endpoints: (builder) => ({
    getAllOrganizations: builder.query<
      { response: GetAllOrganizationsResponse },
      { parentId?: string; pageNo: number; pageSize: number; type: string }
    >({
      query: ({ pageNo, pageSize, type }) => ({
        url: `organization/get-all-organization?type=${type}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
      providesTags: ["getAllOrganizations"],
    }),
    getOrganizationList: builder.query<
      {
        statusCode: number;
        httpStatus: string;
        response: { name: string; id: string; syllabusAssigned: boolean }[];
      },
      { type: string; parentId: string; subjectId?: string }
    >({
      query: ({ type, parentId, subjectId }) => ({
        url: `organization/get-organization-dropdown?type=${type}&parentId=${parentId}&subjectId=${subjectId}`,
        method: "GET",
      }),
    }),
    getAllOrganizationForDropdown: builder.query<
      {
        statusCode: number;
        httpStatus: string;
        response: { name: string; id: string; syllabusAssigned: boolean }[];
      },
      void
    >({
      query: () => ({
        url: `organization/get-all`,
        method: "GET",
      }),
    }),
    getAllOrganizationForSuperTaskManager: builder.query<
      {
        statusCode: number;
        httpStatus: string;
        response: {
          content: {
            organizationId: string;
            name: string;
            tournamentCount: number;
            quizCount: number;
            quizGroupCount: number;
          }[];
        };
      },
      { pageNo: number; pageSize: number }
    >({
      query: ({ pageNo, pageSize }) => ({
        url: `organization/get-organizations?pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    getAllTrainerForOrg: builder.query<
      any,
      { organizationId: string; subjectId: string; isSuperTrainer?: boolean }
    >({
      query: ({ organizationId, subjectId, isSuperTrainer }) => {
        let queryString = `organizationId=${organizationId}&subjectId=${subjectId}`;

        if (isSuperTrainer !== undefined) {
          queryString += `&isSuperTrainer=${isSuperTrainer}`;
        }

        return {
          url: `user/get-trainer-dropdown?${queryString}`,
          method: "GET",
        };
      },
    }),

    getOrganizationById: builder.query<
      { response: Organization },
      { organizationId: string }
    >({
      query: ({ organizationId }) => ({
        url: `organization/${organizationId}`,
        method: "GET",
      }),
    }),

    getOrganizationForUser: builder.query<
      { response: GetOrganizationResponse },
      void
    >({
      query: () => ({
        url: `organization/get-organization-user`,
        method: "GET",
      }),
    }),
    createOrganizations: builder.mutation<
      any,
      { payload: string; adderEmail: string; logo: File | null }
    >({
      query: ({ adderEmail, payload, logo }) => {
        const formData = new FormData();
        formData.append("organizationRequest", payload.toString());
        formData.append("multipartFile", logo!);

        return {
          url: `organization?adderEmail=${adderEmail}`,
          method: "POST",
          body: formData,
        };
      },
    }),
    updateOrganization: builder.mutation<
      any,
      { payload: string; organizationId: string; editorEmail: string; logo?: File | null }
    >({
      query: ({ organizationId, payload, editorEmail, logo }) => {
        const formData = new FormData();
        if (logo) {
          formData.append("multipartFile", logo);
        }
        return {
          url: `organization/update?organizationId=${organizationId}&organizationRequest=${encodeURIComponent(payload)}&editorEmail=${encodeURIComponent(editorEmail)}`,
          method: "PUT",
          body: formData,
        };
      },
    }),
    addAdminToOrg: builder.mutation<any, { payload: addUserType }>({
      query: ({ payload }) => {
        return {
          url: `user`,
          method: "POST",
          body: payload,
        };
      },
    }),
    getAllOrganizationsForSuperTrainer: builder.query<
      OrganizationResponseForSuperTrainer,
      { subjectId: string; pageNo: number; pageSize: number }
    >({
      query: ({ subjectId, pageNo, pageSize }) => ({
        url: `organization/get-orgs?subjectId=${subjectId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    getOrganizationChildren: builder.query<
      {
        httpStatus: string;
        statusCode: number;
        response: OrganizationChild[];
      },
      { parentIds: string[]; childType: string }
    >({
      query: ({ parentIds, childType }) => ({
        url: `organization/get-children?childType=${childType}`,
        method: "POST",
        body: parentIds,
        headers: {
          "Content-Type": "application/json",
        },
      }),
    }),
    getQuizGroupConfiguration: builder.query<
      GetQuizGroupConfigurationResponse,
      void
    >({
      query: () => ({
        url: `organization/get-groupconfigurations`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetAllOrganizationsQuery,
  useLazyGetAllOrganizationsQuery,
  useGetOrganizationByIdQuery,
  useLazyGetOrganizationByIdQuery,
  useGetOrganizationForUserQuery,
  useLazyGetOrganizationListQuery,
  useCreateOrganizationsMutation,
  useAddAdminToOrgMutation,
  useLazyGetAllTrainerForOrgQuery,
  useGetAllOrganizationForSuperTaskManagerQuery,
  useLazyGetAllOrganizationForSuperTaskManagerQuery,
  useUpdateOrganizationMutation,
  useGetAllOrganizationsForSuperTrainerQuery,
  useLazyGetAllOrganizationsForSuperTrainerQuery,
  useLazyGetOrganizationChildrenQuery,
  useGetOrganizationChildrenQuery,
  useLazyGetAllOrganizationForDropdownQuery,
  useLazyGetQuizGroupConfigurationQuery,
  useGetQuizGroupConfigurationQuery,
} = OrganizationApiSlice;
